"use strict";
import { languages } from "../Data/languages.js";
import { roleEmojis, roleLabels, tasksCode, tasksCodeFormal } from "../Data/tasksCode.js";
import { getDownloadUrlForFileWithoutHandler } from "../config/firebase.js";

export function getLanguageName(code) {
  const language = languages.find((lang) => lang.code === code);
  return language ? language.name : "Unknown Language";
}
export function getTaskName(code) {
  if (Array.isArray(code)) {
    return (
      code
        .map((c) => getTaskName(c))
        .filter((name) => name && name !== "Unknown task")
        .join(", ") || "Unknown task"
    );
  }
  const task = tasksCode.find((task) => task.code === code);
  return task ? task.name : "Unknown task";
}
export function getTaskNameFormal(code) {
  if (Array.isArray(code)) {
    return (
      code
        .map((c) => getTaskNameFormal(c))
        .filter((name) => name && name !== "Unknown task")
        .join(", ") || "Unknown task"
    );
  }
  const task = tasksCodeFormal.find((task) => task.code === code);
  return task ? task.name : "Unknown task";
}
export function getRoleOptionLabel(code) {
  const label = roleLabels[code] || getTaskName(code);
  const emoji = roleEmojis[code];

  return emoji ? `${emoji} ${label}` : label;
}

export function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = remainingSeconds.toFixed(2).padStart(5, "0"); // Ensures 2 decimal places

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function timeStringToSeconds(timeString) {
    return parseTimestampAsTotalSeconds(timeString);
}

/**
 * Split a timestamp string into its numeric components.
 *
 * @param {string} timestamp - Timestamp in HH:MM:SS.MS or HH:MM:SS,MS format.
 * @returns {number[]} Array containing hours, minutes, seconds and milliseconds.
 */
export function parseTimestampAsComponents(timestamp) {
    const [hours = "0", minutes = "0", seconds = "0", ms = "0"] =
        (timestamp || "").split(/[:.,]/);

    const milliseconds = parseInt(ms.padEnd(3, "0").slice(0, 3), 10) || 0;

    return [
        parseInt(hours, 10) || 0,
        parseInt(minutes, 10) || 0,
        parseInt(seconds, 10) || 0,
        milliseconds,
    ];
}

/**
 * Convert a timestamp string to total seconds.
 *
 * @param {string} timestamp - Timestamp in HH:MM:SS.MS or HH:MM:SS,MS format.
 * @returns {number} Total seconds represented by the timestamp.
 */
export function parseTimestampAsTotalSeconds(timestamp) {
    const [hours, minutes, seconds, milliseconds] = parseTimestampAsComponents(
        timestamp,
    );
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
}

/**
 * Evaluate a translated audio duration against the original duration.
 *
 * @param {number} originalLength - Length of the original audio in seconds.
 * @param {number} translatedLength - Length of the translated audio in seconds.
 * @returns {"perfect"|"needsLonger"|"needsShorter"|""} The evaluation result.
 */
export function getDurationStatus(originalLength, translatedLength) {
    if (!translatedLength) {
        return "";
    }

    const allowedDifference = originalLength * 0.18;
    const lowerBound = originalLength - allowedDifference;
    const upperBound = originalLength + allowedDifference;

    if (translatedLength >= lowerBound && translatedLength <= upperBound) {
        return "perfect";
    }
    if (translatedLength < lowerBound) {
        return "needsLonger";
    }
    return "needsShorter";
}

export function convertToDate(creationDate) {
  if (creationDate) {
    const millisecondsFromSeconds = creationDate.seconds * 1000;
    const millisecondsFromNanoseconds = creationDate.nanoseconds / 1000000;
    const totalMilliseconds =
      millisecondsFromSeconds + millisecondsFromNanoseconds;
    const dateObject = new Date(totalMilliseconds);
    const formattedDate =
      dateObject.getUTCFullYear() +
      "-" +
      String(dateObject.getUTCMonth() + 1).padStart(2, "0") +
      "-" +
      String(dateObject.getUTCDate()).padStart(2, "0");

    return formattedDate;
  } else return;
}

export async function processPathUrl(input) {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$", // fragment locator
    "i"
  );

  if (urlPattern.test(input)) {
    return input;
  } else {
    try {
      const url = await getDownloadUrlForFileWithoutHandler(input);
      return url;
    } catch (error) {
      // console.error("Error fetching download URL:", error);
      return "loading";
    }
  }
}

export const formatInputs = (input) => {
  return input
    .replace(/\s/g, "_") // Replace spaces with underscores (now first)
    .replace(/[^a-zA-Z0-9_]/g, ""); // Remove any remaining special characters
};
